import Vue from "vue";
import Router from "vue-router";
import store from "./store";
import { setTheme, themedRoutes } from "./helpers/theme";
import { initMatomo, isMatomoReady, trackMatomoPageView } from "./helpers/matomo";
import {
  initGoogleAnalytics,
  isGoogleAnalyticsReady,
  trackGoogleAnalyticsPageView,
} from "./helpers/analytics";
import { hasAnalyticsConsent } from "./helpers/consent";
Vue.use(Router);

const router = new Router({
  mode: "history",
  linkActiveClass: "active",
  scrollBehavior() {
    return { x: 0, y: 0 };
  },

  routes: [
    {
      path: "/",
      name: "home",
      component: () => import("@/pages/Home.vue"),
    },
    {
      path: "/listing",
      name: "listing",
      component: () => import("@/pages/HotelListing.vue"),
    },
    {
      path: "/how-gloveler-works",
      name: "how-gloveler-works",
      component: () => import("@/pages/HowGlovelerWorks.vue"),
    },
    {
      path: "/about-us",
      name: "about-us",
      component: () => import("@/pages/AboutUs.vue"),
    },
    {
      path: "/terms-of-use",
      name: "terms-of-use",
      component: () => import("@/pages/TermsOfUse.vue"),
    },
    {
      path: "/privacy-policy",
      name: "privacy-policy",
      component: () => import("@/pages/PrivacyPolicy.vue"),
    },
    {
      path: "/legal-notice",
      name: "legal-notice",
      component: () => import("@/pages/LegalNotice.vue"),
    },
    {
      path: "/list-accomodation",
      name: "list-accomodation",
      component: () => import("@/pages/ListAccomodation.vue"),
    },
    {
      path: "/build-booking-engine",
      name: "build-booking-engine",
      component: () => import("@/pages/BuildBookingEngine.vue"),
    },
    {
      path: "/listing/:id",
      name: "listing-details",
      component: () => import("@/pages/HotelListingItem.vue"),
    },
    {
      path: "/listing/:id/reservation",
      name: "listing-reservation",
      component: () => import("@/pages/HotelReservation.vue"),
    },
    {
      path: "/payment_page",
      name: "payment",
      component: () => import("@/pages/Payment.vue"),
    },
    {
      path: "/payment_update",
      name: "payment-update",
      component: () => import("@/pages/PaymentUpdatePage.vue"),
    },
    {
      path: "/reservation_success",
      name: "reservation-success",
      component: () => import("@/pages/HotelReservationSuccess.vue"),
    },
    {
      path: "/reservation_cancellation",
      name: "reservation-cancellation",
      component: () => import("@/pages/HotelReservationCancellation.vue"),
    },
    {
      path: "/listing_map",
      name: "listing-map",
      component: () => import("@/pages/HotelListingMap.vue"),
    },
    {
      path: "/reset-translations",
      name: "reset-translations",
      component: () => import("@/pages/TranslationReset.vue"),
    },
    {
      path: "/payment-processing",
      name: "payment-processing",
      component: () => import("@/pages/PaymentProcess.vue"),
    },
    {
      path: "/:pathMatch(.*)*",
      name: "404",
      component: () => import("@/pages/404.vue"),
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  const defaultListIdFromConfig = store.state.config?.currentListId;
  const currentListId = to.query?.list_id || from.query?.list_id || defaultListIdFromConfig;
  const isPageRefresh = !from.name && from.matched.length === 0;
  if (isPageRefresh) {
    return next();
  }

  try {
    if (themedRoutes.includes(to.name) && currentListId) {
      await setTheme(currentListId);
    } else {
      await setTheme();
    }
  } catch (error) {
    console.error(error);
  }

  next();
});

router.afterEach(async (to, from) => {
  // Preserve list_id for themed routes or related routes (child/parent)
  const fromPathBase = from.path ? from.path.split('?')[0] : '';
  const toPathBase = to.path ? to.path.split('?')[0] : '';
  const isChildRoute = fromPathBase && toPathBase.startsWith(fromPathBase) && toPathBase !== fromPathBase;
  const isParentRoute = toPathBase && fromPathBase.startsWith(toPathBase) && fromPathBase !== toPathBase;
  const isRelatedRoute = isChildRoute || isParentRoute;
  const isThemedRoute = themedRoutes.includes(to.name);
  const shouldPreserveListId = isRelatedRoute || (isThemedRoute && from.query.list_id);

  if (shouldPreserveListId && !to.query.list_id && from.query.list_id) {
    const preservedListId = from.query.list_id;
    setTimeout(() => {
      if (!router.currentRoute.query.list_id) {
        router.replace({
          path: to.path,
          query: { ...to.query, list_id: preservedListId },
          params: to.params,
        });
      }
    }, 0);
  }

  const config = store.state.config.currentConfig;
  if (config) {
    await initMatomo(Vue, config);
  }
  if (isMatomoReady()) {
    trackMatomoPageView();
  }

  const analyticsConfig = store.state.config.currentConfig;
  if (analyticsConfig && hasAnalyticsConsent(analyticsConfig)) {
    initGoogleAnalytics(analyticsConfig);
  }
  if (isGoogleAnalyticsReady()) {
    trackGoogleAnalyticsPageView(analyticsConfig);
  }
});

export default router;
